import { useState, useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

type UseTextSearchReplaceProps = {
  editorRef: RefObject<HTMLDivElement>;
  initialContent: string;
  previewPrompt?: string | null;
  isPreviewMode?: boolean;
  onContentChange?: (content: string) => void;
};

export const useTextSearchReplace = ({
  editorRef,
  initialContent,
  onContentChange,
  isPreviewMode,
}: UseTextSearchReplaceProps) => {
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');

  const unescapeSlashes = (text: string): string => {
    return text
      .replace(/^(['"])(.*)\1$/, '$2')
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  };
  const escapeHTML = (str: string) =>
    str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  useEffect(() => {
    if (editorRef.current && !isPreviewMode) {
      const raw = unescapeSlashes(initialContent || '');
      const escaped = escapeHTML(raw).replace(/\n/g, '<br>');
      editorRef.current.innerHTML = escaped;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewMode]);

  useEffect(() => {
    const handleInput = () => {
      if (editorRef.current && onContentChange) {
        const text = editorRef.current.innerText.replace(/\u00A0/g, ' ');
        onContentChange(text.trim() === '' ? '' : text);
      }
    };

    const ref = editorRef.current;
    if (ref && !isPreviewMode) ref.addEventListener('input', handleInput);
    return () => ref?.removeEventListener('input', handleInput);
  }, [editorRef, onContentChange, isPreviewMode]);

  const handleHighlight = useCallback(() => {
    if (!findText.trim() || !editorRef.current || isPreviewMode) return;

    const escapeRegExp = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const content = editorRef.current.innerText;
    const escaped = escapeHTML(content).replace(/\n/g, '<br>');

    const regex = new RegExp(escapeRegExp(findText), 'gi');
    const highlighted = escaped.replace(
      regex,
      match => `<mark style="background: yellow; color: black;">${match}</mark>`
    );

    editorRef.current.innerHTML = highlighted;
  }, [findText, editorRef, isPreviewMode]);

  const handleReplace = useCallback(() => {
    if (!findText.trim() || !editorRef.current || isPreviewMode) return;

    const regex = new RegExp(findText, 'gi');
    const newText = editorRef.current.innerText.replace(regex, replaceText);

    const escaped = escapeHTML(newText).replace(/\n/g, '<br>');
    editorRef.current.innerHTML = escaped;

    onContentChange?.(newText);
  }, [findText, replaceText, editorRef, onContentChange, isPreviewMode]);

  return {
    findText,
    setFindText,
    replaceText,
    setReplaceText,
    handleHighlight,
    handleReplace,
  };
};
